import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ApiError, apiDelete, apiGet, apiPatch, apiPost } from "../api";
import { Skeleton, SkeletonTableRows, Spinner } from "../components/Loaders";
import { NumberStepper } from "../components/NumberStepper";
import { Select } from "../components/Select";
import type {
  CardSearchHit,
  CollectionListResponse,
  CollectionSummary,
  SavedCollectionResponse,
} from "../types";
import { ARENA_EXPORTER_DOWNLOAD_HREF } from "../constants/arenaExporter";

interface ResolvedCard {
  name: string;
  oracle_id: string;
  set: string;
  collector_number: string;
  rarity: string;
  image_url: string | null;
  is_on_arena: boolean;
  type_line: string;
  colors: string[];
}

interface ResolvedEntry {
  input_name: string;
  count: number;
  match_kind: "exact" | "fuzzy" | "unresolved";
  card: ResolvedCard | null;
}

interface ResolveResult {
  totals: {
    total_cards: number;
    unique_cards: number;
    unresolved: number;
    fuzzy: number;
  };
  resolved: ResolvedEntry[];
  unresolved: ResolvedEntry[];
  arena_text: string;
  owned?: Record<string, number>;
  saved?: boolean;
  saved_at?: string | null;
  collection_id?: string | null;
  /** Full flat map after resolve/merge; present on successful API resolve. */
  counts_raw?: Record<string, number>;
}

const PLACEHOLDER = `{
  "Boseiju, Who Endures": 2,
  "Fable of the Mirror-Breaker": 4,
  "Lightning Bolt": 4
}`;

const RARITY_ORDER: Array<{
  key: string;
  label: string;
  cls: string;
}> = [
  { key: "mythic", label: "Mythics", cls: "rarity-pill rarity-mythic" },
  { key: "rare", label: "Rares", cls: "rarity-pill rarity-rare" },
  { key: "uncommon", label: "Uncommons", cls: "rarity-pill rarity-uncommon" },
  { key: "common", label: "Commons", cls: "rarity-pill rarity-common" },
];

type CollectionSort = "name" | "count" | "rarity" | "set";

const SORT_OPTIONS: Array<{ value: CollectionSort; label: string }> = [
  { value: "rarity", label: "Sort: Rarity" },
  { value: "name", label: "Sort: Name" },
  { value: "count", label: "Sort: Copies" },
  { value: "set", label: "Sort: Set" },
];

export function Collection() {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [result, setResult] = useState<ResolveResult | null>(null);
  const [collections, setCollections] = useState<CollectionSummary[]>([]);
  const [selectedCollectionId, setSelectedCollectionId] = useState<string | null>(null);
  const [saved, setSaved] = useState<SavedCollectionResponse | null>(null);
  const [loadingSaved, setLoadingSaved] = useState(true);
  const [resolvingSaved, setResolvingSaved] = useState(false);
  const [deletingCollectionId, setDeletingCollectionId] = useState<string | null>(null);
  const [savingEdits, setSavingEdits] = useState(false);
  const [newCollectionMode, setNewCollectionMode] = useState(false);
  const [importName, setImportName] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [editingCollectionId, setEditingCollectionId] = useState<string | null>(null);
  const [editingCollectionValue, setEditingCollectionValue] = useState("");
  /** Display name at the moment inline rename started; used to no-op on blur/Enter if unchanged. */
  const [editingNameBaseline, setEditingNameBaseline] = useState("");
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [mergeText, setMergeText] = useState("");
  const [mergeBusy, setMergeBusy] = useState(false);
  const [mergeDragOver, setMergeDragOver] = useState(false);
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [mergeModalError, setMergeModalError] = useState<string | null>(null);
  const mergeFileInputRef = useRef<HTMLInputElement | null>(null);

  // Local edits. When non-null, these supersede the last-known server counts
  // and indicate the page has unsaved edits. Cleared on successful save or
  // explicit discard.
  const [workingCounts, setWorkingCounts] =
    useState<Record<string, number> | null>(null);

  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState<CollectionSort>("rarity");

  // Which unresolved row has its inline replacement search open.
  const [fixingIndex, setFixingIndex] = useState<number | null>(null);

  const loadCollectionById = async (
    collectionId: string | null,
    cancelled = false,
  ): Promise<void> => {
    const path = collectionId
      ? `/api/collection?collection_id=${encodeURIComponent(collectionId)}`
      : "/api/collection";
    const data = await apiGet<SavedCollectionResponse>(path);
    if (cancelled) return;
    setSaved(data);
    setLoadingSaved(false);
    setWorkingCounts(null);
    setConfirmDeleteId(null);
    setFixingIndex(null);
    setFilter("");
    if (data.saved && data.counts_raw && Object.keys(data.counts_raw).length) {
      setText(JSON.stringify(data.counts_raw, null, 2));
      setResolvingSaved(true);
      try {
        const fresh = await apiPost<ResolveResult>(
          "/api/collection/resolve",
          { counts: data.counts_raw, save: false },
        );
        if (!cancelled) setResult(fresh);
      } catch {
        // Non-fatal: stats banner still works from the summary totals.
      } finally {
        if (!cancelled) setResolvingSaved(false);
      }
    } else {
      setResult(null);
      setText("");
    }
  };

  const refreshCollections = async (preferredId?: string | null) => {
    const list = await apiGet<CollectionListResponse>("/api/collections");
    const rows = list.collections || [];
    setCollections(rows);
    const preferredExists = !!(
      preferredId && rows.some((r) => r.id === preferredId)
    );
    const selectedExists = !!(
      selectedCollectionId && rows.some((r) => r.id === selectedCollectionId)
    );
    const choice =
      (preferredExists ? preferredId : null) ||
      (selectedExists ? selectedCollectionId : null) ||
      rows[0]?.id ||
      null;
    setSelectedCollectionId(choice);
    return choice;
  };

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      setLoadingSaved(true);
      setResolvingSaved(false);
      try {
        await refreshCollections(null);
        if (cancelled) return;
        setLoadingSaved(false);
      } catch {
        if (!cancelled) {
          setSaved({ saved: false });
          setResult(null);
          setLoadingSaved(false);
          setResolvingSaved(false);
        }
      }
    };

    bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedCollectionId) return;
    let cancelled = false;
    setLoadingSaved(true);
    setResolvingSaved(false);
    loadCollectionById(selectedCollectionId, cancelled).catch(() => {
      if (!cancelled) {
        setSaved({ saved: false });
        setResult(null);
        setLoadingSaved(false);
        setResolvingSaved(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [selectedCollectionId]);

  useEffect(() => {
    if (!saved?.saved) {
      setImportName("");
      setRenameValue("");
      setEditingCollectionId(null);
      setEditingCollectionValue("");
      setEditingNameBaseline("");
      return;
    }
    setImportName(saved.name?.trim() || "");
    setRenameValue(saved.name?.trim() || "");
    setEditingCollectionId(null);
    setEditingCollectionValue("");
    setEditingNameBaseline("");
  }, [saved?.saved, saved?.id, saved?.name]);

  useEffect(() => {
    if (!confirmDeleteId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setConfirmDeleteId(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [confirmDeleteId]);

  useEffect(() => {
    if (!confirmDeleteId) return;
    if (!collections.some((c) => c.id === confirmDeleteId)) {
      setConfirmDeleteId(null);
    }
  }, [confirmDeleteId, collections]);

  const closeMergeModal = () => {
    if (mergeBusy) return;
    setMergeModalOpen(false);
    setMergeDragOver(false);
    setMergeModalError(null);
  };

  useEffect(() => {
    if (!mergeModalOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      if (mergeBusy) return;
      setMergeModalOpen(false);
      setMergeDragOver(false);
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKey);
    };
  }, [mergeModalOpen, mergeBusy]);

  useEffect(() => {
    if (!selectedCollectionId) {
      setMergeModalOpen(false);
      setMergeDragOver(false);
      setMergeModalError(null);
    }
  }, [selectedCollectionId]);

  const blockForUnsavedWork = (message: string): boolean => {
    if (workingCounts === null && !(newCollectionMode && text.trim())) {
      return false;
    }
    setError(message);
    setNotice(null);
    return true;
  };

  const selectCollection = (collectionId: string) => {
    if (collectionId === selectedCollectionId && !newCollectionMode) return;
    if (
      blockForUnsavedWork(
        "Save or discard your current changes before switching collections.",
      )
    ) {
      return;
    }
    setSelectedCollectionId(collectionId);
    setNewCollectionMode(false);
    setConfirmDeleteId(null);
    setEditingCollectionId(null);
    setEditingCollectionValue("");
    setEditingNameBaseline("");
    setNotice(null);
    setError(null);
  };

  const setUpNewCollectionMode = () => {
    if (
      blockForUnsavedWork(
        "Save or discard your current changes before starting a new import.",
      )
    ) {
      return;
    }
    setNewCollectionMode(true);
    setText("");
    setImportName("");
    setError(null);
    setNotice(null);
    setConfirmDeleteId(null);
  };

  const parsed = useMemo(() => _safeParse(text), [text]);
  const mergeParsed = useMemo(() => _safeParse(mergeText), [mergeText]);
  const parseStatus =
    !text.trim()
      ? null
      : parsed.ok
      ? `Parsed ${parsed.count.toLocaleString()} card(s) from ${
          parsed.source === "exporter" ? "mtga_collection.json" : "flat JSON"
        }.`
      : `JSON error: ${parsed.error}`;

  const mergeParseStatus =
    !mergeText.trim()
      ? null
      : mergeParsed.ok
        ? `Ready to add ${mergeParsed.count.toLocaleString()} card name(s) from this JSON.`
        : `JSON error: ${mergeParsed.error}`;

  // Seed workingCounts from the current result (lazily) the first time the
  // user makes an inline edit, so we have a mutable buffer to batch changes
  // into before hitting Save.
  const getOrInitWorking = (): Record<string, number> => {
    if (workingCounts) return { ...workingCounts };
    const base: Record<string, number> = {};
    if (result) {
      for (const e of [...result.resolved, ...result.unresolved]) {
        if (e.count > 0) base[e.input_name] = e.count;
      }
    } else if (saved?.counts_raw) {
      Object.assign(base, saved.counts_raw);
    }
    return base;
  };

  const applyEdit = (
    mutator: (counts: Record<string, number>) => Record<string, number> | void,
  ) => {
    setError(null);
    const next = getOrInitWorking();
    const out = mutator(next);
    const final = out ?? next;
    setWorkingCounts(final);
  };

  // --- inline edits -------------------------------------------------------

  // Change the count of a resolved entry. Its input_name is the map key;
  // setting to 0 removes the row entirely.
  const onChangeResolvedCount = (entry: ResolvedEntry, nextCount: number) => {
    const n = Math.max(0, Math.floor(nextCount));
    applyEdit((counts) => {
      if (n <= 0) {
        delete counts[entry.input_name];
      } else {
        counts[entry.input_name] = n;
      }
    });

    setResult((prev) => {
      if (!prev) return prev;
      const resolved = [...prev.resolved];
      const idx = resolved.findIndex(
        (e) => e.input_name === entry.input_name,
      );
      if (idx < 0) return prev;
      if (n <= 0) {
        resolved.splice(idx, 1);
      } else {
        resolved[idx] = { ...resolved[idx], count: n };
      }
      return _recomputeTotals({ ...prev, resolved });
    });
  };

  const onChangeUnresolvedCount = (entry: ResolvedEntry, nextCount: number) => {
    const n = Math.max(0, Math.floor(nextCount));
    applyEdit((counts) => {
      if (n <= 0) {
        delete counts[entry.input_name];
      } else {
        counts[entry.input_name] = n;
      }
    });

    setResult((prev) => {
      if (!prev) return prev;
      const unresolved = [...prev.unresolved];
      const idx = unresolved.findIndex(
        (e) => e.input_name === entry.input_name,
      );
      if (idx < 0) return prev;
      if (n <= 0) {
        unresolved.splice(idx, 1);
      } else {
        unresolved[idx] = { ...unresolved[idx], count: n };
      }
      return _recomputeTotals({ ...prev, unresolved });
    });
  };

  // Replace an unresolved row with a picked card from the DB. Keeps the
  // original count and folds it into any existing entry for that card.
  const onReplaceUnresolved = (
    entry: ResolvedEntry,
    pick: CardSearchHit,
  ) => {
    const count = entry.count;

    const isBasic = (pick.type_line ?? "").toLowerCase().includes("basic land");
    applyEdit((counts) => {
      delete counts[entry.input_name];
      const total = (counts[pick.name] ?? 0) + count;
      counts[pick.name] = isBasic ? total : Math.min(4, total);
    });

    setResult((prev) => {
      if (!prev) return prev;
      const unresolved = prev.unresolved.filter(
        (e) => e.input_name !== entry.input_name,
      );
      const resolved = [...prev.resolved];
      const existingIdx = resolved.findIndex(
        (e) => e.card?.oracle_id === pick.oracle_id,
      );
      if (existingIdx >= 0) {
        const total = resolved[existingIdx].count + count;
        resolved[existingIdx] = {
          ...resolved[existingIdx],
          count: isBasic ? total : Math.min(4, total),
        };
      } else {
        resolved.push({
          input_name: pick.name,
          count,
          match_kind: "exact",
          card: {
            name: pick.name,
            oracle_id: pick.oracle_id,
            set: pick.set ?? "",
            collector_number: "",
            rarity: (pick.rarity ?? "").toLowerCase(),
            image_url: pick.image_url ?? null,
            is_on_arena: !!pick.is_on_arena,
            type_line: pick.type_line ?? "",
            colors: pick.colors ?? [],
          },
        });
      }
      return _recomputeTotals({ ...prev, resolved, unresolved });
    });

    setFixingIndex(null);
  };

  // --- save / discard / bulk actions -------------------------------------

  const saveEdits = async () => {
    if (!workingCounts) return;
    const snapshot = workingCounts;
    setSavingEdits(true);
    setError(null);
    try {
      const data = await apiPost<ResolveResult>("/api/collection/resolve", {
        counts: snapshot,
        save: true,
        name: renameValue.trim() || saved?.name || "",
        collection_id: selectedCollectionId ?? undefined,
      });
      const newSelected = data.collection_id ?? null;
      await refreshCollections(newSelected);
      setResult(data);
      setSaved({
        saved: true,
        id: data.collection_id ?? undefined,
        name: renameValue.trim() || saved?.name || "",
        updated_at: data.saved_at ?? null,
        counts_raw: snapshot,
        totals: data.totals,
        arena_text: data.arena_text,
      });
      setText(JSON.stringify(snapshot, null, 2));
      setWorkingCounts(null);
      setNotice("Collection changes saved.");
    } catch (e) {
      setError((e as ApiError | Error).message);
    } finally {
      setSavingEdits(false);
    }
  };

  const discardEdits = async () => {
    if (!saved?.counts_raw) {
      setWorkingCounts(null);
      return;
    }
    setSavingEdits(true);
    setError(null);
    try {
      const fresh = await apiPost<ResolveResult>(
        "/api/collection/resolve",
        { counts: saved.counts_raw, save: false },
      );
      setResult(fresh);
      setWorkingCounts(null);
    } catch (e) {
      setError((e as ApiError | Error).message);
    } finally {
      setSavingEdits(false);
    }
  };

  const onResolve = async () => {
    if (!parsed.ok) {
      setError("Paste a valid JSON object of {\"Card Name\": count, ...} first.");
      return;
    }
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      const data = await apiPost<ResolveResult>("/api/collection/resolve", {
        counts: parsed.counts,
        save: true,
        name: importName.trim(),
      });
      const newSelected = data.collection_id ?? null;
      await refreshCollections(newSelected);
      setResult(data);
      setWorkingCounts(null);
      if (data.saved) {
        const resolvedName = importName.trim();
        setSaved({
          saved: true,
          id: data.collection_id ?? undefined,
          name: resolvedName,
          updated_at: data.saved_at ?? null,
          counts_raw: parsed.counts,
          totals: data.totals,
          arena_text: data.arena_text,
        });
      }
      setNewCollectionMode(false);
    } catch (e) {
      setError((e as ApiError | Error).message);
    } finally {
      setBusy(false);
    }
  };

  const onDeleteCollection = async (collectionId: string) => {
    if (!collectionId) return;
    if (
      collectionId === selectedCollectionId &&
      blockForUnsavedWork(
        "Save or discard your current changes before deleting this collection.",
      )
    ) {
      return;
    }
    const targetIdx = collections.findIndex((c) => c.id === collectionId);
    const target = targetIdx >= 0 ? collections[targetIdx] : null;
    setDeletingCollectionId(collectionId);
    setError(null);
    setNotice(null);
    try {
      await apiDelete(`/api/collection?collection_id=${encodeURIComponent(collectionId)}`);
      const deletingSelected = collectionId === selectedCollectionId;
      const nextId = await refreshCollections(deletingSelected ? null : selectedCollectionId);
      setConfirmDeleteId(null);
      if (deletingSelected && !nextId) {
        setSaved({ saved: false });
        setResult(null);
        setResolvingSaved(false);
        setWorkingCounts(null);
        setText("");
        setImportName("");
        setNewCollectionMode(true);
        setNotice("Collection deleted. Import a new file to continue.");
      } else {
        setNotice(
          `${_collectionDisplayName(target, Math.max(0, targetIdx), collections.length)} deleted.`,
        );
      }
    } catch (e) {
      setError((e as ApiError | Error).message);
    } finally {
      setDeletingCollectionId(null);
    }
  };

  /** Toggle delete confirmation for a collection. Blocked if that collection is open with unsaved edits. */
  const requestDeleteCollection = (collectionId: string) => {
    if (
      collectionId === selectedCollectionId &&
      blockForUnsavedWork(
        "Save or discard your current changes before deleting this collection.",
      )
    ) {
      return;
    }
    setConfirmDeleteId((c) => (c === collectionId ? null : collectionId));
    setError(null);
    setNotice(null);
  };

  const cancelCollectionRename = () => {
    setEditingCollectionId(null);
    setEditingCollectionValue("");
    setEditingNameBaseline("");
  };

  const beginInlineRename = (collection: CollectionSummary, idx: number) => {
    setEditingCollectionId(collection.id);
    const label = _collectionDisplayName(collection, idx, collections.length);
    setEditingCollectionValue(label);
    setEditingNameBaseline(label);
    setError(null);
    setNotice(null);
  };

  const onRenameCollection = async (collectionId: string, nextRaw: string) => {
    const next = nextRaw.trim();
    if (!next) {
      setError("Collection name cannot be empty.");
      return;
    }
    const previous = collections.find((c) => c.id === collectionId);
    const previousNorm = (previous?.name ?? "").trim();
    if (next === previousNorm) {
      cancelCollectionRename();
      return;
    }
    setRenaming(true);
    setError(null);
    setNotice(null);
    try {
      await apiPatch("/api/collection", {
        collection_id: collectionId,
        name: next,
      });
      await refreshCollections(collectionId);
      setSaved((prev) =>
        prev && prev.id === collectionId ? { ...prev, name: next } : prev,
      );
      setRenameValue(next);
      cancelCollectionRename();
      setNotice("Collection name updated.");
    } catch (e) {
      setError((e as ApiError | Error).message);
    } finally {
      setRenaming(false);
    }
  };

  const handleImportFile = async (file: File) => {
    const isJsonLike =
      file.name.toLowerCase().endsWith(".json") ||
      file.type.includes("json") ||
      file.type.includes("text");
    if (!isJsonLike) {
      setError("Upload a JSON file (scanner output or mtga_collection.json).");
      setNotice(null);
      return;
    }
    try {
      const fileText = await file.text();
      setText(fileText);
      setError(null);
      setNotice(`Loaded ${file.name}. Review and click ${hasSaved ? '"Import as new collection"' : '"Save collection"'}.`);
    } catch {
      setError(`Could not read ${file.name}.`);
      setNotice(null);
    }
  };

  const handleMergeImportFile = async (file: File) => {
    const isJsonLike =
      file.name.toLowerCase().endsWith(".json") ||
      file.type.includes("json") ||
      file.type.includes("text");
    if (!isJsonLike) {
      setMergeModalError("Upload a JSON file to merge into this collection.");
      setNotice(null);
      return;
    }
    try {
      const fileText = await file.text();
      setMergeText(fileText);
      setMergeModalError(null);
      setNotice(`Loaded ${file.name} for merge. Review and click "Update List".`);
    } catch {
      setMergeModalError(`Could not read ${file.name}.`);
      setNotice(null);
    }
  };

  const onMergeIntoCollection = async () => {
    if (
      blockForUnsavedWork(
        "Save or discard your current edits before merging a new import.",
      )
    ) {
      return;
    }
    if (!selectedCollectionId) {
      setMergeModalError("Select a saved collection first.");
      return;
    }
    if (!mergeParsed.ok) {
      setMergeModalError("Paste valid JSON to add (flat map or exporter array).");
      setNotice(null);
      return;
    }
    setMergeBusy(true);
    setMergeModalError(null);
    setNotice(null);
    try {
      const before = saved?.counts_raw ?? {};
      const data = await apiPost<ResolveResult>("/api/collection/resolve", {
        counts: mergeParsed.counts,
        save: true,
        merge: true,
        collection_id: selectedCollectionId,
        name: renameValue.trim() || saved?.name || "",
      });
      const merged = data.counts_raw ?? {};
      const delta = _summarizeMerge(before, merged);
      await refreshCollections(selectedCollectionId);
      await loadCollectionById(selectedCollectionId, false);
      setMergeText("");
      setMergeModalOpen(false);
      setMergeDragOver(false);
      setNotice(
        `Update List complete: +${delta.copiesAdded.toLocaleString()} copies across ${delta.changedNames.toLocaleString()} card name${delta.changedNames === 1 ? "" : "s"}${delta.newNames > 0 ? ` (${delta.newNames.toLocaleString()} new)` : ""}.`,
      );
    } catch (e) {
      setMergeModalError((e as ApiError | Error).message);
    } finally {
      setMergeBusy(false);
    }
  };

  const rarityCounts = useMemo(() => _rarityCounts(result), [result]);
  const tableRows = useMemo(
    () => _filteredSortedRows(result, filter, sort),
    [result, filter, sort],
  );

  // Diff between pending edits and the last server-persisted snapshot so
  // the user can review exactly what "Save changes" is about to write.
  const pendingChanges = useMemo(
    () => _diffCounts(saved?.counts_raw, workingCounts, result),
    [saved?.counts_raw, workingCounts, result],
  );

  const hasSaved = !!saved?.saved;
  const showingExistingCollection = hasSaved && !newCollectionMode;
  const savedUnique =
    result?.totals.unique_cards ?? saved?.totals?.unique_cards ?? 0;
  const savedTotal =
    result?.totals.total_cards ?? saved?.totals?.total_cards ?? 0;
  const savedWhen = saved?.updated_at
    ? new Date(saved.updated_at).toLocaleString()
    : null;

  const dirty = workingCounts !== null;
  const collectionOptions = collections.map((c, idx) => ({
    value: c.id,
    label:
      `${c.name?.trim() || `Import ${collections.length - idx}`}` +
      (c.totals?.unique_cards ? ` · ${c.totals.unique_cards} unique` : ""),
  }));

  const activeCollection =
    collections.find((c) => c.id === selectedCollectionId) ?? null;
  const activeCollectionIdx = activeCollection
    ? collections.indexOf(activeCollection)
    : -1;
  const activeCollectionName = _collectionDisplayName(
    activeCollection,
    activeCollectionIdx >= 0 ? activeCollectionIdx : 0,
    collections.length,
  );
  const isEditingActiveCollection =
    !!selectedCollectionId && editingCollectionId === selectedCollectionId;
  const deletePrompt = useMemo(() => {
    if (!confirmDeleteId) return null;
    const dIdx = collections.findIndex((c) => c.id === confirmDeleteId);
    if (dIdx < 0) return null;
    const t = collections[dIdx];
    return {
      id: t.id,
      name: _collectionDisplayName(t, dIdx, collections.length),
    };
  }, [confirmDeleteId, collections]);
  const deletePromptBusy = deletePrompt
    ? deletingCollectionId === deletePrompt.id
    : false;
  const showImportWorkspace = !loadingSaved && (!hasSaved || newCollectionMode);
  const showReview =
    showingExistingCollection &&
    result &&
    (result.unresolved.length > 0 || result.totals.fuzzy > 0);

  return (
    <>
    <main className="page collection-page">
      <header className="collection-page-head">
        <div>
          <h1>Your MTG Arena collection</h1>
          <p className="collection-page-subtitle">
            Saved Arena imports, match review, and card counts.
          </p>
        </div>
      </header>

      <div className="collection-workspace">
      <aside className="collection-sidebar collection-panel" aria-label="Saved collection imports">
        <div className="collection-sidebar-head">
          <h2>
            Collections <span className="collection-sidebar-total">({collections.length})</span>
          </h2>
        </div>
        {collections.length > 0 ? (
          <div className="collection-rail-list">
            {collections.map((collection, idx) => {
              const isActive = collection.id === selectedCollectionId;
              const isShowingActive = isActive && !newCollectionMode;
              const displayName = _collectionDisplayName(
                collection,
                idx,
                collections.length,
              );
              const confirmTarget = confirmDeleteId === collection.id;
              return (
                <article
                  key={collection.id}
                  className={`collection-rail-row${isShowingActive ? " active" : ""}${confirmTarget ? " collection-rail-row-confirm-target" : ""}`}
                >
                  <button
                    type="button"
                    className="collection-rail-main"
                    onClick={() => selectCollection(collection.id)}
                    aria-current={isShowingActive ? "true" : undefined}
                  >
                    <span className="collection-rail-name">{displayName}</span>
                    <span className="collection-rail-meta">
                      {_collectionStatsText(collection)}
                    </span>
                    {collection.updated_at && (
                      <span className="collection-rail-date">
                        {_formatDate(collection.updated_at)}
                      </span>
                    )}
                  </button>
                </article>
              );
            })}
          </div>
        ) : (
          <p className="collection-sidebar-empty">Create an import to start.</p>
        )}
        <button
          type="button"
          className="primary"
          onClick={setUpNewCollectionMode}
          disabled={newCollectionMode}
        >
          New import
        </button>
      </aside>

      <div className="collection-main">

      {dirty && (
        <div className="dirty-bar" role="status">
          <div className="dirty-bar-text">
            <strong>
              {pendingChanges.length === 0
                ? "No net changes to save."
                : `${pendingChanges.length} unsaved change${
                    pendingChanges.length === 1 ? "" : "s"
                  }`}
            </strong>
            {pendingChanges.length > 0 && (
              <span style={{ color: "var(--muted, #789)", marginLeft: 8 }}>
                Review before saving.
              </span>
            )}
          </div>
          {pendingChanges.length > 0 && (
            <ul className="dirty-bar-changes">
              {pendingChanges.slice(0, 12).map((c) => (
                <li key={c.name} className={`dirty-change dirty-${c.kind}`}>
                  <span className="dirty-change-tag" aria-hidden="true">
                    {c.kind === "added" && "+"}
                    {c.kind === "removed" && "−"}
                    {c.kind === "changed" && "~"}
                  </span>
                  <span className="dirty-change-delta">
                    {c.kind === "added" && `+${c.to}`}
                    {c.kind === "removed" && `-${c.from}`}
                    {c.kind === "changed" && `${c.from} → ${c.to}`}
                  </span>
                  <span className="dirty-change-name">{c.displayName}</span>
                </li>
              ))}
              {pendingChanges.length > 12 && (
                <li className="dirty-change dirty-more">
                  ...and {pendingChanges.length - 12} more
                </li>
              )}
            </ul>
          )}
          <div className="dirty-bar-actions">
            <button
              className="secondary"
              onClick={discardEdits}
              disabled={savingEdits}
            >
              Discard
            </button>
            <button
              type="button"
              className="primary"
              onClick={saveEdits}
              disabled={savingEdits || pendingChanges.length === 0}
            >
              {savingEdits ? (
                <>
                  <Spinner className="button-spinner" />
                  Saving...
                </>
              ) : (
                "Save changes"
              )}
            </button>
          </div>
        </div>
      )}

      {error && !dirty && (
        <div className="error-banner collection-flow-alert">{error}</div>
      )}
      {notice && (
        <div
          className="info-banner info-banner--dismissible collection-flow-alert"
          role="status"
        >
          <p className="info-banner-text">{notice}</p>
          <button
            type="button"
            className="secondary small"
            onClick={() => setNotice(null)}
            aria-label="Dismiss notification"
          >
            Dismiss
          </button>
        </div>
      )}

      {deletePrompt && (
        <div
          className="collection-panel collection-inline-confirm collection-delete-prompt"
          role="alertdialog"
          aria-labelledby="collection-delete-prompt-title"
        >
          <p id="collection-delete-prompt-title">
            Delete <strong>{deletePrompt.name}</strong>? Deck or inventory views that
            used this import may need to be re-pointed.
          </p>
          <p className="collection-delete-prompt-escape-hint" role="note">
            Press Escape to cancel.
          </p>
          <div className="collection-inline-confirm-actions">
            <button
              type="button"
              className="danger"
              onClick={() => void onDeleteCollection(deletePrompt.id)}
              disabled={deletePromptBusy}
            >
              {deletePromptBusy ? "Deleting…" : "Delete import"}
            </button>
            <button
              type="button"
              className="secondary"
              onClick={() => setConfirmDeleteId(null)}
              disabled={deletePromptBusy}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {showImportWorkspace && (
        <section className="collection-panel collection-import-panel">
          <div className="collection-import-head">
            <div>
              <span className="collection-eyebrow">New import</span>
              <h2 style={{ margin: 0 }}>
                {hasSaved ? "Create collection import" : "Import from Arena exporter JSON"}
              </h2>
              <p>Formats: flat JSON, mtga_collection.json</p>
            </div>
            <div className="collection-import-actions">
              <a className="topbar-btn primary" href={ARENA_EXPORTER_DOWNLOAD_HREF} download>
                Download Arena exporter
              </a>
              {hasSaved && (
                <button
                  type="button"
                  className="secondary"
                  onClick={() => {
                    setNewCollectionMode(false);
                    setError(null);
                    setNotice(null);
                    setText("");
                    setImportName(saved?.name?.trim() || "");
                  }}
                >
                  Cancel
                </button>
              )}
            </div>
          </div>

          <div className="collection-import-grid">
            <label className="field">
              <span>Collection name</span>
              <input
                type="text"
                placeholder='e.g. "Main account", "Alt account", "Wife account"'
                value={importName}
                onChange={(e) => setImportName(e.target.value)}
                maxLength={120}
              />
            </label>
            <div className="collection-import-format">
              <span>Source</span>
              <p>
                The Windows exporter is vendored in{" "}
                <code>tools/mtga-collection-exporter/</code> (derived from{" "}
                <a
                  href="https://github.com/NthPhantom10/MTGA-collection-exporter"
                  target="_blank"
                  rel="noreferrer"
                >
                  MTGA Collection Exporter
                </a>
                , MIT). Runs locally; paste JSON here only.
              </p>
            </div>
          </div>

          <label className="field collection-paste-field">
            <span>Paste JSON</span>
            <textarea
              rows={10}
              placeholder={PLACEHOLDER}
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
          </label>

          <div
            className={`collection-upload-dropzone ${isDragOver ? "drag-over" : ""}`}
            onDragOver={(e) => {
              e.preventDefault();
              if (!isDragOver) setIsDragOver(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              setIsDragOver(false);
            }}
            onDrop={(e) => {
              e.preventDefault();
              setIsDragOver(false);
              const file = e.dataTransfer.files?.[0];
              if (file) void handleImportFile(file);
            }}
          >
            <p>
              Drop scanner JSON here, or{" "}
              <button
                type="button"
                className="linkish-btn"
                onClick={() => fileInputRef.current?.click()}
              >
                choose a file
              </button>
              .
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json,application/json,text/plain"
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleImportFile(file);
                e.currentTarget.value = "";
              }}
            />
          </div>

          {parseStatus && (
            <p className={`collection-parse-status${parsed.ok ? "" : " error"}`}>
              {parseStatus}
            </p>
          )}

          <div className="collection-form-actions">
            <button
              type="button"
              className="primary"
              onClick={onResolve}
              disabled={busy || !parsed.ok}
            >
              {busy ? (
                <>
                  <Spinner className="button-spinner" />
                  Saving...
                </>
              ) : hasSaved ? (
                "Import as new collection"
              ) : (
                "Save collection"
              )}
            </button>
            <button
              type="button"
              className="secondary"
              onClick={() => {
                setText("");
                setError(null);
                if (newCollectionMode) setImportName("");
              }}
              disabled={busy}
            >
              Clear
            </button>
          </div>
        </section>
      )}

      {loadingSaved && <CollectionInitialSkeleton />}

      {!loadingSaved && showingExistingCollection && (
        <section
          className="collection-panel collection-summary"
          aria-busy={resolvingSaved}
        >
          <div className="collection-summary-head">
            <div className="collection-summary-main">
              <div className="collection-summary-top">
                <div>
                  <div className="collection-summary-name-row">
                    {isEditingActiveCollection && selectedCollectionId ? (
                      <input
                        type="text"
                        className="collection-summary-name-input"
                        value={editingCollectionValue}
                        maxLength={120}
                        autoFocus
                        onChange={(e) => setEditingCollectionValue(e.target.value)}
                        onBlur={() => {
                          if (renaming) return;
                          const t = editingCollectionValue.trim();
                          const b = editingNameBaseline.trim();
                          if (t === "" || t === b) {
                            cancelCollectionRename();
                            return;
                          }
                          void onRenameCollection(selectedCollectionId, editingCollectionValue);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            if (renaming) return;
                            const t = editingCollectionValue.trim();
                            const b = editingNameBaseline.trim();
                            if (t === "" || t === b) {
                              cancelCollectionRename();
                              return;
                            }
                            void onRenameCollection(selectedCollectionId, editingCollectionValue);
                          } else if (e.key === "Escape") {
                            e.preventDefault();
                            cancelCollectionRename();
                          }
                        }}
                      />
                    ) : (
                      <>
                        <h2 className="collection-summary-name">{activeCollectionName}</h2>
                        <button
                          type="button"
                          className="collection-summary-edit"
                          aria-label={`Rename ${activeCollectionName}`}
                          title={`Rename ${activeCollectionName}`}
                          onClick={() => {
                            if (activeCollection && activeCollectionIdx >= 0) {
                              beginInlineRename(activeCollection, activeCollectionIdx);
                            }
                          }}
                          disabled={!activeCollection}
                        >
                          ✎
                        </button>
                      </>
                    )}
                  </div>
                  <p className="collection-summary-hint">
                    Rename this import, or use{" "}
                    <button
                      type="button"
                      className="linkish-btn"
                      onClick={() => {
                        if (selectedCollectionId) {
                          requestDeleteCollection(selectedCollectionId);
                        }
                      }}
                      disabled={!selectedCollectionId}
                    >
                      delete
                    </button>{" "}
                    and confirm in the red banner.
                  </p>
                </div>
                {selectedCollectionId && (
                  <div className="collection-summary-actions">
                    <button
                      type="button"
                      className="secondary collection-summary-trash"
                      aria-label={`Delete ${activeCollectionName}`}
                      title={`Delete ${activeCollectionName}`}
                      onClick={() => requestDeleteCollection(selectedCollectionId)}
                    >
                      ✕
                    </button>
                    <button
                      type="button"
                      className="secondary"
                      onClick={() => {
                        if (
                          blockForUnsavedWork(
                            "Save or discard your current edits before merging a new import.",
                          )
                        ) {
                          return;
                        }
                        setError(null);
                        setMergeModalError(null);
                        setMergeModalOpen(true);
                      }}
                    >
                      Update List
                    </button>
                  </div>
                )}
              </div>
              {resolvingSaved && !result && !saved?.totals ? (
                <div className="collection-summary-counts loading">
                  <Skeleton variant="title" width={220} />
                </div>
              ) : (
                <div className="collection-summary-counts">
                  <strong>{savedUnique.toLocaleString()}</strong>{" "}
                  <span>unique cards</span>
                  {savedTotal > 0 && (
                    <>
                      {" · "}
                      <strong>{savedTotal.toLocaleString()}</strong>{" "}
                      <span>copies</span>
                    </>
                  )}
                </div>
              )}
              {savedWhen && (
                <div className="collection-summary-date">
                  Last updated {savedWhen}
                </div>
              )}
              {resolvingSaved && (
                <div className="collection-summary-status" role="status">
                  <Spinner className="button-spinner" />
                  Preparing card details...
                </div>
              )}
            </div>
          </div>

          {rarityCounts && (
            <div className="collection-rarity-row">
              {RARITY_ORDER.map(({ key, label, cls }) => (
                <span key={key} className={cls}>
                  <strong>{rarityCounts[key].toLocaleString()}</strong> {label}
                </span>
              ))}
            </div>
          )}
          {!rarityCounts && resolvingSaved && (
            <div className="collection-rarity-row loading" aria-hidden="true">
              {RARITY_ORDER.map(({ key }) => (
                <Skeleton key={key} variant="chip" width={118} />
              ))}
            </div>
          )}
        </section>
      )}

      {error && dirty && (
        <div className="error-banner">{error}</div>
      )}

      {showReview && result && (
        <section className="collection-panel collection-review">
          <div className="collection-section-head">
            <div>
              <span className="collection-eyebrow">Review</span>
              <h2>Matches</h2>
            </div>
            <div className="collection-review-badges">
              {result.unresolved.length > 0 && (
                <span className="rarity-pill rarity-danger">
                  {result.unresolved.length} unresolved
                </span>
              )}
              {result.totals.fuzzy > 0 && (
                <span className="rarity-pill rarity-warn">
                  {result.totals.fuzzy} fuzzy
                </span>
              )}
            </div>
          </div>

          {result.unresolved.length > 0 && (
            <div className="collection-review-block">
              <h3>Unresolved cards</h3>
              <ul className="unresolved-list">
                {result.unresolved.map((e, i) => (
                  <li key={`${e.input_name}-${i}`} className="unresolved-row">
                    <div className="unresolved-top">
                      <code className="unresolved-name">{e.input_name}</code>
                      <div className="unresolved-count">
                        <NumberStepper
                          value={String(e.count)}
                          onChange={(v) =>
                            onChangeUnresolvedCount(e, Number(v) || 0)
                          }
                          min={0}
                          max={99}
                          ariaLabel={`Copies of ${e.input_name}`}
                        />
                        <button
                          className="secondary small"
                          type="button"
                          onClick={() =>
                            setFixingIndex(fixingIndex === i ? null : i)
                          }
                        >
                          {fixingIndex === i ? "Cancel" : "Fix"}
                        </button>
                        <button
                          className="secondary small"
                          type="button"
                          onClick={() => onChangeUnresolvedCount(e, 0)}
                          title="Remove this unresolved entry entirely"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                    {fixingIndex === i && (
                      <InlineCardSearch
                        onPick={(card) => onReplaceUnresolved(e, card)}
                      />
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {result.totals.fuzzy > 0 && (
            <details className="collection-fuzzy-details">
              <summary>Fuzzy matches</summary>
              <ul>
                {result.resolved
                  .filter((e) => e.match_kind === "fuzzy")
                  .map((e, i) => (
                    <li key={i}>
                      <code>{e.input_name}</code> -&gt;{" "}
                      <strong>{e.card?.name}</strong> x {e.count}
                    </li>
                  ))}
              </ul>
            </details>
          )}
        </section>
      )}

      {showingExistingCollection && resolvingSaved && !result && (
        <CollectionCardsSkeleton />
      )}

      {showingExistingCollection && result && result.resolved.length > 0 && (
        <section className="collection-panel collection-cards-panel">
          <div className="collection-section-head">
            <div>
              <span className="collection-eyebrow">Cards</span>
              <h2>Resolved collection</h2>
            </div>
            <div className="collection-cards-toolbar">
              <input
                type="text"
                placeholder="Filter by name, type, or set..."
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
              <Select
                value={sort}
                onChange={setSort}
                ariaLabel="Sort cards"
                minWidth={168}
                options={SORT_OPTIONS}
              />
            </div>
          </div>
          <div className="collection-table-wrap">
            <table className="collection-table">
              <thead>
                <tr>
                  <th className="col-name">Name</th>
                  <th>Rarity</th>
                  <th>Set</th>
                  <th className="col-count">Copies</th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((row) => (
                  <tr key={row.oracle_id}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{row.name}</div>
                      {row.type_line && (
                        <div
                          style={{
                            fontSize: "0.78rem",
                            color: "var(--muted, #789)",
                          }}
                        >
                          {row.type_line}
                        </div>
                      )}
                      {row.input_name &&
                        row.input_name.toLowerCase() !== row.name.toLowerCase() && (
                          <div
                            className="fuzzy-hint"
                            title={
                              row.match_kind === "fuzzy"
                                ? "The scanner produced a slightly different name; we fuzzy-matched to this card."
                                : "The scanner used this card's MTG Arena (Alchemy) name."
                            }
                          >
                            matched from <code>{row.input_name}</code>
                          </div>
                        )}
                    </td>
                    <td>
                      <span className={`rarity-chip rarity-${row.rarity}`}>
                        {row.rarity}
                      </span>
                    </td>
                    <td style={{ color: "var(--muted, #789)" }}>
                      {row.set.toUpperCase()}
                    </td>
                    <td className="col-count">
                      <NumberStepper
                        value={String(row.count)}
                        onChange={(v) =>
                          onChangeResolvedCount(row.entry, Number(v) || 0)
                        }
                        min={0}
                        max={99}
                        ariaLabel={`Copies of ${row.name}`}
                      />
                    </td>
                  </tr>
                ))}
                {tableRows.length === 0 && (
                  <tr>
                    <td
                      colSpan={4}
                      style={{
                        textAlign: "center",
                        color: "var(--muted, #789)",
                        padding: "16px 12px",
                      }}
                    >
                      No cards match "{filter}".
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Unresolved cards get their own dedicated section so the "click to
          fix" affordance is obvious, instead of hiding behind a diagnostics
          dropdown. Lives under the main Cards list so the happy-path
          content is visible first. */}
      {showingExistingCollection && result && false && result!.totals.fuzzy > 0 && (
        <details className="result">
          <summary
            style={{
              cursor: "pointer",
              fontWeight: 600,
              fontSize: "1.05rem",
            }}
          >
            Fuzzy matches
            <span className="rarity-pill rarity-warn" style={{ marginLeft: 8 }}>
              {result!.totals.fuzzy} fuzzy
            </span>
          </summary>
          <p style={{ color: "var(--muted, #789)" }}>
            These names weren&apos;t exact but close enough that we guessed
            the matching card. Review in case the scanner picked the wrong one.
          </p>
          <ul>
            {result!.resolved
              .filter((e) => e.match_kind === "fuzzy")
              .map((e, i) => (
                <li key={i}>
                  <code>{e.input_name}</code> -&gt;{" "}
                  <strong>{e.card?.name}</strong> × {e.count}
                </li>
              ))}
          </ul>
        </details>
      )}

      {/* Re-import lives at the bottom of the page: once a user has imported
          once, re-pasting a new scanner dump is a rare action, so we keep
          the dedicated Cards + Unresolved views front-and-center and tuck
          the paste-JSON workflow below them. */}
      {false && showImportWorkspace && (
      <section className="result" style={{ marginTop: 16 }}>
        <div className="collection-import-head">
          <h2 style={{ margin: 0 }}>
            {hasSaved
              ? "Create new collection import"
              : "Import from the Arena collection exporter"}
          </h2>
          {hasSaved && (
            <button
              type="button"
              className="secondary"
              onClick={() => {
                setNewCollectionMode(false);
                setError(null);
                setNotice(null);
                setText("");
                setImportName(saved?.name?.trim() || "");
              }}
            >
              Cancel
            </button>
          )}
        </div>
        <p style={{ marginTop: 0 }}>
          Import from either scanner/exporter format: a flat{" "}
          <code>{"{name: count}"}</code> object or the{" "}
          <code>mtga_collection.json</code> array format from{" "}
          <a
            href="https://github.com/NthPhantom10/MTGA-collection-exporter"
            target="_blank"
            rel="noreferrer"
          >
            MTGA Collection Exporter
          </a>
          .
        </p>
        <p style={{ marginTop: 0, color: "var(--muted, #789)", fontSize: "0.85rem" }}>
          Uses{" "}
          <a
            href="https://github.com/NthPhantom10/MTGA-collection-exporter"
            target="_blank"
            rel="noreferrer"
          >
            MTGA Collection Exporter
          </a>{" "}
          format support under the MIT License.
        </p>
        <p>
          <a className="topbar-btn primary" href={ARENA_EXPORTER_DOWNLOAD_HREF} download>
            Download MTGDeckBuilder
          </a>{" "}
          <span style={{ color: "var(--muted, #789)" }}>
            Windows executable (~9 MB). Runs locally; nothing is uploaded by the exporter.
          </span>
        </p>
        <label className="field">
          <span>Collection name</span>
          <input
            type="text"
            placeholder='e.g. "Main account", "Alt account", "Wife account"'
            value={importName}
            onChange={(e) => setImportName(e.target.value)}
            maxLength={120}
          />
        </label>
        <label className="field">
          <span>Paste JSON</span>
          <textarea
            rows={8}
            placeholder={PLACEHOLDER}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </label>
        <div
          className={`collection-upload-dropzone ${isDragOver ? "drag-over" : ""}`}
          onDragOver={(e) => {
            e.preventDefault();
            if (!isDragOver) setIsDragOver(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setIsDragOver(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragOver(false);
            const file = e.dataTransfer.files?.[0];
            if (file) void handleImportFile(file);
          }}
        >
          <p>
            Drag and drop scanner JSON here, or{" "}
            <button
              type="button"
              className="linkish-btn"
              onClick={() => fileInputRef.current?.click()}
            >
              choose a file
            </button>
            .
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json,application/json,text/plain"
            style={{ display: "none" }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleImportFile(file);
              e.currentTarget.value = "";
            }}
          />
        </div>
        {parseStatus && (
          <p
            style={{
              marginTop: 4,
              color: parsed.ok ? "var(--muted, #789)" : "var(--danger, #d04848)",
            }}
          >
            {parseStatus}
          </p>
        )}
        {notice && <p className="info-banner">{notice}</p>}
        <div className="actions" style={{ marginTop: 12 }}>
          <button
            type="button"
            className="primary"
            onClick={onResolve}
            disabled={busy || !parsed.ok}
          >
            {busy ? (
              <>
                <Spinner className="button-spinner" />
                Saving...
              </>
            ) : hasSaved ? (
              "Import as new collection"
            ) : (
              "Save collection"
            )}
          </button>
          <button
            className="secondary"
            onClick={() => {
              setText("");
              setError(null);
              if (newCollectionMode) setImportName("");
            }}
            disabled={busy}
          >
            Clear textarea
          </button>
        </div>
        {error && !dirty && (
          <div className="error-banner">{error}</div>
        )}
      </section>
      )}
      </div>
      </div>
    </main>

    {mergeModalOpen &&
      selectedCollectionId &&
      createPortal(
        <div
          className="collection-merge-modal-backdrop"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeMergeModal();
          }}
        >
          <div
            className="collection-merge-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="collection-merge-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="collection-merge-modal-head">
              <div>
                <span className="collection-eyebrow">Update import</span>
                <h2 id="collection-merge-modal-title">Update List</h2>
              </div>
              <button
                type="button"
                className="collection-merge-modal-close"
                onClick={closeMergeModal}
                disabled={mergeBusy}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <p className="collection-merge-modal-hint">
              Counts in this file are <strong>added</strong> to{" "}
              <strong>{activeCollectionName}</strong>. Playset limits still
              apply when resolved. For a full replacement, use{" "}
              <strong>New import</strong> in the sidebar.
            </p>

            {mergeModalError && (
              <div className="collection-merge-modal-error" role="alert">
                {mergeModalError}
              </div>
            )}

            <label className="field collection-paste-field">
              <span>Paste JSON</span>
              <textarea
                rows={7}
                placeholder={PLACEHOLDER}
                value={mergeText}
                onChange={(e) => {
                  setMergeText(e.target.value);
                  setMergeModalError(null);
                }}
                disabled={mergeBusy}
              />
            </label>

            <div
              className={`collection-upload-dropzone ${mergeDragOver ? "drag-over" : ""}`}
              onDragOver={(e) => {
                e.preventDefault();
                if (!mergeDragOver) setMergeDragOver(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                setMergeDragOver(false);
              }}
              onDrop={(e) => {
                e.preventDefault();
                setMergeDragOver(false);
                const file = e.dataTransfer.files?.[0];
                if (file) void handleMergeImportFile(file);
              }}
            >
              <p>
                Drop a file here, or{" "}
                <button
                  type="button"
                  className="linkish-btn"
                  onClick={() => mergeFileInputRef.current?.click()}
                >
                  choose a file
                </button>
                .
              </p>
              <input
                ref={mergeFileInputRef}
                type="file"
                accept=".json,application/json,text/plain"
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void handleMergeImportFile(file);
                  e.currentTarget.value = "";
                }}
              />
            </div>

            {mergeParseStatus && (
              <p
                className={`collection-parse-status${mergeParsed.ok ? "" : " error"}`}
              >
                {mergeParseStatus}
              </p>
            )}

            <p className="collection-merge-modal-escape-hint" role="note">
              Press Escape to close.
            </p>

            <div className="collection-form-actions collection-merge-modal-actions">
              <button
                type="button"
                className="primary"
                onClick={() => void onMergeIntoCollection()}
                disabled={mergeBusy || !mergeParsed.ok}
              >
                {mergeBusy ? (
                  <>
                    <Spinner className="button-spinner" />
                    Merging…
                  </>
                ) : (
                  "Update List"
                )}
              </button>
              <button
                type="button"
                className="secondary"
                onClick={() => {
                  setMergeText("");
                  setMergeModalError(null);
                }}
                disabled={mergeBusy || !mergeText.trim()}
              >
                Clear
              </button>
              <button
                type="button"
                className="secondary"
                onClick={closeMergeModal}
                disabled={mergeBusy}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

function CollectionInitialSkeleton() {
  return (
    <section className="result collection-loading-panel" aria-busy="true">
      <div className="collection-loading-head">
        <Spinner className="button-spinner" />
        <span>Checking saved collection...</span>
      </div>
      <div className="collection-loading-grid" aria-hidden="true">
        <Skeleton variant="title" width={220} />
        <Skeleton variant="chip" width={118} />
        <Skeleton variant="chip" width={118} />
      </div>
    </section>
  );
}

function CollectionCardsSkeleton() {
  return (
    <section className="result collection-cards-skeleton" aria-busy="true">
      <div className="collection-table-head">
        <div>
          <h2>Cards</h2>
          <div className="collection-summary-status" role="status">
            <Spinner className="button-spinner" />
            Loading resolved cards...
          </div>
        </div>
        <div className="collection-table-tools" aria-hidden="true">
          <Skeleton variant="block" width={220} height={40} />
          <Skeleton variant="block" width={168} height={40} />
        </div>
      </div>
      <div className="collection-table-wrap">
        <table className="collection-table">
          <thead>
            <tr>
              <th className="col-name">Name</th>
              <th>Rarity</th>
              <th>Set</th>
              <th className="col-count">Copies</th>
            </tr>
          </thead>
          <tbody>
            <SkeletonTableRows rows={8} cols={4} />
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Inline card search: lighter-weight than CardPicker for "replace this
// unresolved row" use-case. No sticky selected-chip UI; first pick calls
// onPick and we're done.
// ---------------------------------------------------------------------------

function InlineCardSearch({
  onPick,
}: {
  onPick: (card: CardSearchHit) => void;
}) {
  const [input, setInput] = useState("");
  const [results, setResults] = useState<CardSearchHit[]>([]);
  const [loading, setLoading] = useState(false);
  const seqRef = useRef(0);

  useEffect(() => {
    const q = input.trim();
    if (q.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    const seq = ++seqRef.current;
    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        const data = await apiGet<CardSearchHit[]>(
          `/api/cards/search?q=${encodeURIComponent(q)}&limit=10`,
        );
        if (seqRef.current !== seq) return;
        setResults(data);
      } catch {
        if (seqRef.current !== seq) return;
        setResults([]);
      } finally {
        if (seqRef.current === seq) setLoading(false);
      }
    }, 200);
    return () => clearTimeout(handle);
  }, [input]);

  return (
    <div className="inline-card-search">
      <input
        autoFocus
        type="text"
        value={input}
        placeholder="Search for the correct card..."
        onChange={(e) => setInput(e.target.value)}
        autoComplete="off"
        spellCheck={false}
      />
      {loading && (
        <div className="inline-card-search-status">Searching...</div>
      )}
      {!loading && input.trim().length >= 2 && results.length === 0 && (
        <div className="inline-card-search-status">No cards found.</div>
      )}
      {results.length > 0 && (
        <ul className="inline-card-search-results">
          {results.map((r) => (
            <li key={r.oracle_id}>
              <button
                type="button"
                className="inline-card-search-row"
                onClick={() => onPick(r)}
              >
                {r.image_url ? (
                  <img
                    src={r.image_url}
                    alt={`${r.name} card art`}
                    width={34}
                    loading="lazy"
                  />
                ) : (
                  <span className="inline-card-search-thumb-placeholder" />
                )}
                <span className="inline-card-search-name">{r.name}</span>
                <span className="inline-card-search-meta">
                  {r.set ? r.set.toUpperCase() : ""}
                  {r.rarity ? ` · ${r.rarity}` : ""}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function _collectionDisplayName(
  collection: CollectionSummary | null,
  idx: number,
  total: number,
): string {
  const clean = collection?.name?.trim();
  if (clean) return clean;
  return `Import ${Math.max(1, total - idx)}`;
}

function _collectionStatsText(collection: CollectionSummary): string {
  const unique = collection.totals?.unique_cards ?? 0;
  const total = collection.totals?.total_cards ?? 0;
  if (unique > 0 && total > 0) {
    return `${unique.toLocaleString()} unique - ${total.toLocaleString()} copies`;
  }
  if (unique > 0) return `${unique.toLocaleString()} unique`;
  return "No resolved cards";
}

type MergeSummary = {
  copiesAdded: number;
  changedNames: number;
  newNames: number;
};

function _summarizeMerge(
  before: Record<string, number> | null | undefined,
  after: Record<string, number> | null | undefined,
): MergeSummary {
  const b = before ?? {};
  const a = after ?? {};
  let copiesAdded = 0;
  let changedNames = 0;
  let newNames = 0;
  for (const [name, raw] of Object.entries(a)) {
    const next = Math.max(0, Math.floor(Number(raw) || 0));
    const prev = Math.max(0, Math.floor(Number(b[name]) || 0));
    const delta = next - prev;
    if (delta <= 0) continue;
    copiesAdded += delta;
    changedNames += 1;
    if (prev <= 0) newNames += 1;
  }
  return { copiesAdded, changedNames, newNames };
}

function _formatDate(value: string | null | undefined): string {
  if (!value) return "";
  return new Date(value).toLocaleString();
}

interface PendingChange {
  name: string; // raw key (input_name) in the counts map
  displayName: string; // paper name if we could resolve it, else the raw key
  kind: "added" | "removed" | "changed";
  from: number; // count before (0 for "added")
  to: number; // count after (0 for "removed")
}

function _diffCounts(
  before: Record<string, number> | null | undefined,
  after: Record<string, number> | null,
  result: ResolveResult | null,
): PendingChange[] {
  if (!after) return [];
  const prev = before ?? {};

  // Prefer displaying the resolved paper name so a Fix-replacement reads as
  // "+4 Spider-Verse" instead of "+4 Arachnomania" when the DB collapsed the
  // Arena alias to its paper card.
  const displayFor: Record<string, string> = {};
  if (result) {
    for (const e of result.resolved) {
      if (e.card) displayFor[e.input_name] = e.card.name;
    }
    for (const e of result.unresolved) {
      displayFor[e.input_name] = e.input_name;
    }
  }

  const keys = new Set<string>([...Object.keys(prev), ...Object.keys(after)]);
  const out: PendingChange[] = [];
  for (const name of keys) {
    const from = Math.max(0, Math.floor(prev[name] ?? 0));
    const to = Math.max(0, Math.floor(after[name] ?? 0));
    if (from === to) continue;
    const displayName = displayFor[name] ?? name;
    if (from === 0 && to > 0) {
      out.push({ name, displayName, kind: "added", from, to });
    } else if (to === 0 && from > 0) {
      out.push({ name, displayName, kind: "removed", from, to });
    } else {
      out.push({ name, displayName, kind: "changed", from, to });
    }
  }
  // Stable-ish order: additions first, then changes, then removals; then by
  // display name within each group.
  const order: Record<PendingChange["kind"], number> = {
    added: 0,
    changed: 1,
    removed: 2,
  };
  out.sort(
    (a, b) =>
      order[a.kind] - order[b.kind] || a.displayName.localeCompare(b.displayName),
  );
  return out;
}

const RARITY_RANK: Record<string, number> = {
  mythic: 0,
  rare: 1,
  uncommon: 2,
  common: 3,
};

interface Row {
  oracle_id: string;
  name: string;
  set: string;
  rarity: string;
  count: number;
  type_line: string;
  match_kind: ResolvedEntry["match_kind"];
  input_name: string;
  entry: ResolvedEntry;
}

function _rarityCounts(result: ResolveResult | null): Record<string, number> | null {
  if (!result) return null;
  const counts: Record<string, number> = {
    mythic: 0,
    rare: 0,
    uncommon: 0,
    common: 0,
  };
  for (const e of result.resolved) {
    if (!e.card) continue;
    const r = (e.card.rarity || "").toLowerCase();
    if (r in counts) counts[r] += e.count;
  }
  return counts;
}

function _filteredSortedRows(
  result: ResolveResult | null,
  filter: string,
  sort: "name" | "count" | "rarity" | "set",
): Row[] {
  if (!result) return [];
  const q = filter.trim().toLowerCase();
  const rows: Row[] = [];
  for (const e of result.resolved) {
    if (!e.card) continue;
    const row: Row = {
      oracle_id: e.card.oracle_id,
      name: e.card.name,
      set: e.card.set || "",
      rarity: (e.card.rarity || "unknown").toLowerCase(),
      count: e.count,
      type_line: e.card.type_line || "",
      match_kind: e.match_kind,
      input_name: e.input_name,
      entry: e,
    };
    if (!q) {
      rows.push(row);
      continue;
    }
    const hay =
      `${row.name} ${row.type_line} ${row.set}`.toLowerCase();
    if (hay.includes(q)) rows.push(row);
  }

  rows.sort((a, b) => {
    switch (sort) {
      case "name":
        return a.name.localeCompare(b.name);
      case "count":
        return b.count - a.count || a.name.localeCompare(b.name);
      case "set":
        return (
          a.set.localeCompare(b.set) ||
          (RARITY_RANK[a.rarity] ?? 9) - (RARITY_RANK[b.rarity] ?? 9) ||
          a.name.localeCompare(b.name)
        );
      case "rarity":
      default:
        return (
          (RARITY_RANK[a.rarity] ?? 9) - (RARITY_RANK[b.rarity] ?? 9) ||
          a.name.localeCompare(b.name)
        );
    }
  });
  return rows;
}

function _recomputeTotals(result: ResolveResult): ResolveResult {
  let total = 0;
  let unique = 0;
  let fuzzy = 0;
  for (const e of result.resolved) {
    if (e.count <= 0) continue;
    total += e.count;
    unique += 1;
    if (e.match_kind === "fuzzy") fuzzy += e.count;
  }
  let unresolvedCount = 0;
  for (const e of result.unresolved) {
    if (e.count > 0) unresolvedCount += 1;
  }
  return {
    ...result,
    totals: {
      total_cards: total,
      unique_cards: unique,
      unresolved: unresolvedCount,
      fuzzy,
    },
  };
}

type ParsedCounts =
  | { ok: true; counts: Record<string, number>; count: number; source: "flat" | "exporter" }
  | { ok: false; error: string };

function _safeParse(text: string): ParsedCounts {
  const trimmed = text.trim();
  if (!trimmed) return { ok: false, error: "empty" };
  let data: unknown;
  try {
    data = JSON.parse(trimmed);
  } catch (e) {
    return { ok: false, error: (e as Error).message };
  }
  const counts: Record<string, number> = {};

  if (Array.isArray(data)) {
    for (const row of data) {
      if (!row || typeof row !== "object") continue;
      const name = String((row as { name?: unknown }).name ?? "").trim();
      if (!name) continue;
      const rawCount = (row as { count?: unknown }).count;
      const n = typeof rawCount === "number" ? rawCount : Number(rawCount);
      if (!Number.isFinite(n) || n <= 0) continue;
      counts[name] = (counts[name] ?? 0) + Math.round(n);
    }
    return { ok: true, counts, count: Object.keys(counts).length, source: "exporter" };
  }

  if (data == null || typeof data !== "object") {
    return {
      ok: false,
      error:
        "expected either {name: count} or an array of {name, count} entries (mtga_collection.json)",
    };
  }

  for (const [k, v] of Object.entries(data)) {
    const n = typeof v === "number" ? v : Number(v);
    if (!Number.isFinite(n) || n <= 0) continue;
    counts[k] = Math.round(n);
  }
  return { ok: true, counts, count: Object.keys(counts).length, source: "flat" };
}
